const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface Notification {
    id: number;
    type: string;
    title: string;
    message: string | null;
    data: Record<string, any> | null;
    actionUrl: string | null;
    icon: string | null;
    isRead: boolean;
    createdAt: string;
    readAt: string | null;
}

export interface NotificationResponse {
    notifications: Notification[];
    unreadCount: number;
}

const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
    };
};

export const notificationService = {
    /**
     * Get all notifications for the current user
     */
    async getNotifications(unreadOnly: boolean = false, limit: number = 50): Promise<NotificationResponse> {
        const params = new URLSearchParams();
        if (unreadOnly) params.append('unread_only', 'true');
        if (limit) params.append('limit', limit.toString());

        const response = await fetch(`${API_URL}/api/notifications?${params}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error('Failed to fetch notifications');
        }

        return response.json();
    },

    /**
     * Get unread notification count
     */
    async getUnreadCount(): Promise<number> {
        const response = await fetch(`${API_URL}/api/notifications/unread-count`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error('Failed to fetch unread count');
        }

        const data = await response.json();
        return data.count;
    },

    /**
     * Mark a notification as read
     */
    async markAsRead(notificationId: number): Promise<void> {
        const response = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error('Failed to mark notification as read');
        }
    },

    /**
     * Mark all notifications as read
     */
    async markAllAsRead(): Promise<number> {
        const response = await fetch(`${API_URL}/api/notifications/mark-all-read`, {
            method: 'POST',
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error('Failed to mark all notifications as read');
        }

        const data = await response.json();
        return data.markedCount;
    },

    /**
     * Get icon for notification type
     */
    getIconForType(type: string): string {
        const iconMap: Record<string, string> = {
            course_completed: 'school',
            certificate_earned: 'card_membership',
            badge_earned: 'emoji_events',
            learning_path_completed: 'route',
            streak_milestone: 'local_fire_department',
            enrollment_reminder: 'alarm',
            new_course_available: 'new_releases',
            group_invitation: 'group_add',
            peer_review_assigned: 'rate_review',
            peer_review_received: 'rate_review',
            system: 'info',
        };
        return iconMap[type] || 'notifications';
    },

    /**
     * Get color for notification type
     */
    getColorForType(type: string): string {
        const colorMap: Record<string, string> = {
            course_completed: '#4caf50',
            certificate_earned: '#ff9800',
            badge_earned: '#9c27b0',
            learning_path_completed: '#2196f3',
            streak_milestone: '#f44336',
            enrollment_reminder: '#607d8b',
            new_course_available: '#00bcd4',
            group_invitation: '#795548',
            peer_review_assigned: '#3f51b5',
            peer_review_received: '#009688',
            system: '#9e9e9e',
        };
        return colorMap[type] || '#757575';
    },
};

export default notificationService;
